import { ref, onUnmounted, onMounted } from 'vue';

export interface PollResult {
  notify?: boolean;
  totalPending?: number | null;
  onUpdate?: () => void;
  title?: string;
  body?: string;
}

export function usePolling(fetchCallback: () => Promise<PollResult | void | null>) {
  let intervalId: ReturnType<typeof setInterval> | null = null;
  const checkInterval = Number(import.meta.env.VITE_CHECK_INTERVAL) || 60000;
  const lastUpdate = ref('');
  let lastKnownCount: number | null = null;

  const startPolling = async () => {
    // Pedir permiso de notificaciones de forma no bloqueante
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission().catch(e => console.warn('Notif permission ignored', e));
      }
    }

    const executePoll = async () => {
      try {
        const result = await fetchCallback();
        
        lastUpdate.value = new Date().toLocaleTimeString(undefined, { hour12: false });
        
        if (result && typeof result.totalPending === 'number') {
          const count = result.totalPending;
          if (lastKnownCount !== null && count > lastKnownCount) {
            if (Notification.permission === 'granted') {
              new Notification(result.title || 'Nueva Notificación', { 
                body: result.body || 'Hay nuevas actualizaciones disponibles en el sistema.' 
              });
            }
            if (result.onUpdate) {
              result.onUpdate();
            }
          }
          lastKnownCount = count;
        } else if (result && result.notify && Notification.permission === 'granted') {
          new Notification(result.title || 'Nueva Notificación', { 
            body: result.body || 'Hay nuevas actualizaciones disponibles en el sistema.' 
          });
          if (result.onUpdate) {
              result.onUpdate();
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    await executePoll();
    intervalId = setInterval(executePoll, checkInterval);
  };

  const stopPolling = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  onMounted(() => {
    startPolling();
  });

  onUnmounted(() => {
    stopPolling();
  });

  return {
    startPolling,
    stopPolling,
    lastUpdate,
  };
}
